import React, { useState } from 'react';
import { ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../../../components/Rodal/Rodal';
import ButtonLoader from '../../../../components/Layout/Buttons/ButtonLoader';
import { LoiComparisonFormDto } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { hasFairValueColumn } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import { selectAndExecuteLoi } from '../../../../slices/marketplace/loi-comparison/loi-comparison-api';
import { ConsiderationSummaryTable } from '../LoiComparisonSummary/ConsiderationSummaryTable';
import { SfasFeeParams } from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import './SelectExecuteLoiPopup.scss';

const POPUP_WIDTH = 1100;
const TOOLTIP_ID_SUFFIX = '-select-execute';

type PopupStage = 'confirming' | 'success';

export interface SelectExecuteLoiPopupProps {
    isVisible: boolean;
    onClose: () => void;
    item: LoiComparisonFormDto;
    levelOfService: number;
    sfasParams?: SfasFeeParams | null;
    engagementId: number;
    eventId: string;
    onSuccess: () => void;
}

const SelectExecuteLoiPopup = (props: SelectExecuteLoiPopupProps) => {
    const { isVisible, onClose, item, levelOfService, sfasParams = null, engagementId, eventId, onSuccess } = props;
    const [stage, setStage] = useState<PopupStage>('confirming');
    const [isLoading, setIsLoading] = useState(false);
    const axios = useStateSelector((state) => state.core.axios);

    const showFairValue = hasFairValueColumn(levelOfService);

    const handleConfirm = async () => {
        setIsLoading(true);
        try {
            await selectAndExecuteLoi(axios, engagementId, eventId, item.buyerId);
            onSuccess();
            setStage('success');
        } catch {
            createNotification('Failed to select LOI. Please try again.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const renderSummaryTable = () => (
        <div className="select-execute-loi-popup__table-scroll">
            <ConsiderationSummaryTable
                item={item}
                levelOfService={levelOfService}
                sfasParams={sfasParams}
                showFairValue={showFairValue}
                renderLabelCells
                showLiabilityPayoffs
                tooltipIdSuffix={TOOLTIP_ID_SUFFIX}
                className="select-execute-loi-popup__table"
            />
        </div>
    );

    const renderConfirmingStage = () => (
        <ModalBody>
            <p>
                Are you sure that you want to accept the following offer from {item.buyerName}.
            </p>
            {renderSummaryTable()}
            <div className="mt-4 d-flex justify-content-end buttons-container">
                <button
                    className="btn btn-outline-secondary mr-2"
                    type="button"
                    disabled={isLoading}
                    onClick={onClose}>
                    Cancel
                </button>
                <ButtonLoader
                    className="btn btn-primary"
                    buttonText="Confirm"
                    loaderButtonText="Confirming..."
                    isLoading={isLoading}
                    disabled={false}
                    onClick={handleConfirm}
                />
            </div>
        </ModalBody>
    );

    const renderSuccessStage = () => (
        <ModalBody>
            <p>We will make the LOI available for your signature within 24 hours.</p>
            <div className="mt-4 d-flex justify-content-end buttons-container">
                <button
                    className="btn btn-primary"
                    type="button"
                    onClick={onClose}>
                    Yes, OK
                </button>
            </div>
        </ModalBody>
    );

    return (
        isVisible && (
            <Rodal
                visible={true}
                animation="slideTop"
                showCloseButton={false}
                width={POPUP_WIDTH}
                onClose={onClose}>
                <ModalHeader>
                    <b>Select &amp; Execute LOI</b>
                </ModalHeader>
                {stage === 'confirming' ? renderConfirmingStage() : renderSuccessStage()}
            </Rodal>
        )
    );
};

export default SelectExecuteLoiPopup;
